import React, { useState } from "react";
import { Info, X, ExternalLink } from "lucide-react";

/**
 * MethodologyInfo — компактен info-icon бутон, който отваря модал с
 * методология + ограничения за дадена секция (BHI / BDI / BPP).
 *
 * iter701 — превръща клиничната честност от backend feature в видим
 * trust signal за пациента.
 *
 * Props:
 *   • methodology    — обект с { formula?, inputs_used?, what_we_do_NOT_use?,
 *                                 comparison_to_established_tools?, band_thresholds?,
 *                                 documentation? }
 *   • limitations    — масив от низове (всеки = един limitation bullet)
 *   • tonePolicy     — optional обект { constraint, forbidden_terms, allowed_outcome_verbs }
 *   • title          — заглавие на модала (по подразбиране "За методологията")
 *   • lang           — "bg" | "en" | "es"
 *   • isDark         — boolean
 *   • testid         — uникален data-testid суфикс
 *   • size           — "xs" | "sm" (default "xs")
 */
const TX = {
  bg: {
    title: "За методологията",
    formula: "Формула",
    inputs: "Какво използваме",
    notUsed: "Какво НЕ използваме",
    comparison: "Сравнение с утвърдени инструменти",
    bands: "Прагове на категориите",
    limitations: "Ограничения",
    tone: "Езикова политика",
    forbidden: "Забранени термини",
    softVerbs: "Допустими меки глаголи",
    docs: "Пълна методология",
    close: "Затвори",
    button: "методология",
  },
  en: {
    title: "About the methodology",
    formula: "Formula",
    inputs: "What we use",
    notUsed: "What we do NOT use",
    comparison: "Comparison to established tools",
    bands: "Band thresholds",
    limitations: "Limitations",
    tone: "Tone policy",
    forbidden: "Forbidden terms",
    softVerbs: "Allowed soft verbs",
    docs: "Full methodology",
    close: "Close",
    button: "methodology",
  },
  es: {
    title: "Sobre la metodología",
    formula: "Fórmula",
    inputs: "Qué usamos",
    notUsed: "Qué NO usamos",
    comparison: "Comparación con herramientas establecidas",
    bands: "Umbrales de banda",
    limitations: "Limitaciones",
    tone: "Política de tono",
    forbidden: "Términos prohibidos",
    softVerbs: "Verbos suaves permitidos",
    docs: "Metodología completa",
    close: "Cerrar",
    button: "metodología",
  },
};

export default function MethodologyInfo({
  methodology = {},
  limitations = [],
  tonePolicy = null,
  title,
  lang = "bg",
  isDark = false,
  testid = "default",
  size = "xs",
}) {
  const [open, setOpen] = useState(false);
  const t = TX[lang] || TX.bg;
  const modalTitle = title || t.title;
  const hasContent =
    Object.keys(methodology || {}).length > 0 ||
    (limitations && limitations.length > 0) ||
    tonePolicy;
  if (!hasContent) return null;

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)] text-white"
    : "bg-white border-gray-200 text-gray-900";

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`methodology-info-btn-${testid}`}
        title={modalTitle}
        className={`inline-flex items-center gap-1 rounded-full font-medium transition-all ${
          size === "sm" ? "px-2 py-1 text-[11px]" : "px-1.5 py-0.5 text-[10px]"
        } ${
          isDark
            ? "bg-blue-500/15 text-blue-300 hover:bg-blue-500/25"
            : "bg-blue-50 text-blue-700 hover:bg-blue-100"
        }`}
      >
        <Info className={size === "sm" ? "w-3 h-3" : "w-2.5 h-2.5"} />
        <span className="uppercase tracking-wider">{t.button}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`methodology-info-modal-${testid}`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-2xl border shadow-2xl ${cardCls}`}
          >
            <div className="sticky top-0 flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200/30 backdrop-blur-md">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-500" />
                {modalTitle}
              </h3>
              <button
                onClick={() => setOpen(false)}
                data-testid={`methodology-info-close-${testid}`}
                className={`p-1.5 rounded-lg transition ${
                  isDark ? "hover:bg-white/10" : "hover:bg-gray-100"
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 text-sm">
              {methodology.formula && (
                <Section title={t.formula} isDark={isDark}>
                  <code className={`block px-3 py-2 rounded-lg text-xs font-mono ${
                    isDark ? "bg-black/30 text-emerald-300" : "bg-gray-100 text-emerald-700"
                  }`}>
                    {methodology.formula}
                  </code>
                  {methodology.input_normalisation && (
                    <p className="text-xs opacity-70 mt-2">{methodology.input_normalisation}</p>
                  )}
                </Section>
              )}

              {methodology.band_thresholds && (
                <Section title={t.bands} isDark={isDark}>
                  <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(methodology.band_thresholds).map(([band, threshold]) => (
                      <li
                        key={band}
                        className={`px-2.5 py-1.5 rounded-lg text-xs ${
                          isDark ? "bg-white/5" : "bg-gray-50"
                        }`}
                      >
                        <div className="font-semibold">{band}</div>
                        <div className="opacity-60">≥ {threshold}</div>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {methodology.inputs_used && (
                <Section title={t.inputs} isDark={isDark}>
                  {typeof methodology.inputs_used === "object" && !Array.isArray(methodology.inputs_used) ? (
                    <div className="space-y-2">
                      {Object.entries(methodology.inputs_used).map(([k, vals]) => (
                        <div key={k}>
                          <div className="text-xs font-semibold opacity-70 mb-1 capitalize">{k}</div>
                          <ul className="flex flex-wrap gap-1.5">
                            {(Array.isArray(vals) ? vals : [String(vals)]).map((v, i) => (
                              <li
                                key={i}
                                className={`px-2 py-0.5 rounded-full text-[11px] ${
                                  isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                                }`}
                              >
                                {v}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="flex flex-wrap gap-1.5">
                      {(methodology.inputs_used || []).map((v, i) => (
                        <li
                          key={i}
                          className={`px-2 py-0.5 rounded-full text-[11px] ${
                            isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                          }`}
                        >
                          {v}
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              )}

              {methodology.what_we_do_NOT_use && (
                <Section title={t.notUsed} isDark={isDark} accent="amber">
                  <ul className="space-y-1.5">
                    {methodology.what_we_do_NOT_use.map((v, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <span className="text-amber-500 mt-0.5">✗</span>
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {methodology.comparison_to_established_tools && (
                <Section title={t.comparison} isDark={isDark}>
                  <p className="text-xs leading-relaxed opacity-80">
                    {methodology.comparison_to_established_tools}
                  </p>
                </Section>
              )}

              {tonePolicy && (
                <Section title={t.tone} isDark={isDark}>
                  {tonePolicy.forbidden_terms && (
                    <div className="mb-2">
                      <div className="text-[11px] font-semibold opacity-70 mb-1">{t.forbidden}</div>
                      <div className="flex flex-wrap gap-1">
                        {tonePolicy.forbidden_terms.map((v, i) => (
                          <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] ${
                            isDark ? "bg-rose-500/15 text-rose-300" : "bg-rose-50 text-rose-700"
                          }`}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {tonePolicy.allowed_outcome_verbs && (
                    <div>
                      <div className="text-[11px] font-semibold opacity-70 mb-1">{t.softVerbs}</div>
                      <div className="flex flex-wrap gap-1">
                        {tonePolicy.allowed_outcome_verbs.map((v, i) => (
                          <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] ${
                            isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                          }`}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>
              )}

              {limitations && limitations.length > 0 && (
                <Section title={t.limitations} isDark={isDark} accent="amber">
                  <ul className="space-y-2" data-testid={`methodology-limitations-${testid}`}>
                    {limitations.map((l, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs leading-relaxed">
                        <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
                        <span className="opacity-90">{l}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {methodology.documentation && (
                <div className={`pt-4 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
                  <div className={`text-xs flex items-start gap-2 ${
                    isDark ? "text-blue-300" : "text-blue-600"
                  }`}>
                    <ExternalLink className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      {t.docs}: <code className="opacity-80">{methodology.documentation}</code>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, children, isDark, accent }) {
  const accentColor = accent === "amber"
    ? (isDark ? "text-amber-300" : "text-amber-700")
    : (isDark ? "text-gray-300" : "text-gray-700");
  return (
    <section>
      <h4 className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${accentColor}`}>
        {title}
      </h4>
      {children}
    </section>
  );
}
